import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import {
  MFAChallengeResponse,
  MFAConfig,
  MFAConfigUpdate,
  MFAFactor,
  MFAFactorCreateRequestWithChallengeResponse,
  MFAFactorUpdateRequest,
  MFAFactorVerifyResponse,
  MFAStatus,
  MFAStatusRequest,
} from '../../models/MFA'
import { BaseRequest } from '../models'

const _BASE_URL = import.meta.env.REACT_APP_AUTH_API_BASE_URL!

export interface MFAFactorCreateRequestWithAccountId extends BaseRequest {
  request: MFAFactorCreateRequestWithChallengeResponse
}

export interface FactorRequest extends BaseRequest {
  factorId: string
}

export interface MFAFactorUpdateRequestWithAccountAndFactorId extends FactorRequest {
  request: MFAFactorUpdateRequest
}

export interface MFAFactorDeleteRequestWithAccountId extends FactorRequest {
  challengeResponse: MFAChallengeResponse
}

export interface GenerateRecoveryCodeForUserRequest extends BaseRequest {
  userId: string
}

export interface ListMFAStatusesRequestWithAccountId extends BaseRequest {
  request: MFAStatusRequest
}

export interface MFAFactoryVerifyRequest extends FactorRequest {
  code: string
}

export interface MFAConfigUpdateRequestWithAccountId extends BaseRequest {
  config: MFAConfigUpdate
}

export const mfaApi = createApi({
  reducerPath: 'mfaApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['MFAFactor', 'MFAConfig'],
  endpoints: (builder) => ({
    createFactor: builder.mutation<MFAFactor, MFAFactorCreateRequestWithAccountId>({
      query: ({ accountId, request }) => ({
        url: `/v2/accounts/${accountId}/mfa/factors`,
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: { data: MFAFactor }) => response.data,
      invalidatesTags: ['MFAFactor'],
    }),
    listFactors: builder.query<MFAFactor[], BaseRequest>({
      query: ({ accountId }) => ({
        url: `/v2/accounts/${accountId}/mfa/factors`,
        method: 'GET',
      }),
      transformResponse: (response: { data: MFAFactor[] }) => response.data,
      providesTags: ['MFAFactor'],
    }),
    getFactor: builder.query<MFAFactor, FactorRequest>({
      query: ({ accountId, factorId }) => ({
        url: `/v2/accounts/${accountId}/mfa/factors/${factorId}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: MFAFactor }) => response.data,
      providesTags: ['MFAFactor'],
    }),
    updateFactor: builder.mutation<MFAFactor, MFAFactorUpdateRequestWithAccountAndFactorId>({
      query: ({ accountId, factorId, request }) => ({
        url: `/v2/accounts/${accountId}/mfa/factors/${factorId}`,
        method: 'PATCH',
        body: request,
      }),
      transformResponse: (response: { data: MFAFactor }) => response.data,
      invalidatesTags: ['MFAFactor'],
    }),
    deleteFactor: builder.mutation<void, MFAFactorDeleteRequestWithAccountId>({
      query: ({ accountId, factorId, challengeResponse }) => ({
        url: `/v2/accounts/${accountId}/mfa/factors/${factorId}`,
        method: 'DELETE',
        body: challengeResponse,
      }),
      invalidatesTags: ['MFAFactor'],
    }),
    generateRecoveryCodeForUser: builder.mutation<string, GenerateRecoveryCodeForUserRequest>({
      query: ({ accountId, userId }) => ({
        url: `/v2/accounts/${accountId}/users/${userId}/mfa/recovery`,
        method: 'POST',
      }),
      transformResponse: (response: { data: string }) => response.data,
    }),
    listMFAStatuses: builder.query<MFAStatus[], ListMFAStatusesRequestWithAccountId>({
      query: ({ accountId, request }) => ({
        url: `/v2/accounts/${accountId}/mfa/status`,
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: { data: MFAStatus[] }) => response.data,
    }),
    startFactorChallenge: builder.mutation<void, FactorRequest>({
      query: ({ accountId, factorId }) => ({
        url: `/v2/accounts/${accountId}/mfa/factors/${factorId}/start`,
        method: 'POST',
      }),
    }),
    verifyFactor: builder.mutation<MFAFactorVerifyResponse, MFAFactoryVerifyRequest>({
      query: ({ accountId, factorId, code }) => ({
        url: `/v2/accounts/${accountId}/mfa/factors/${factorId}/verify`,
        method: 'POST',
        body: { code },
      }),
      transformResponse: (response: { data: MFAFactorVerifyResponse }) => response.data,
      invalidatesTags: ['MFAFactor'],
    }),
    getConfig: builder.query<MFAConfig, string>({
      query: (accountId: string) => ({
        url: `/v2/accounts/${accountId}/mfa/config`,
        method: 'GET',
      }),
      transformResponse: (response: { data: MFAConfig }) => response.data,
      providesTags: ['MFAConfig'],
    }),
    updateConfig: builder.mutation<MFAConfig, MFAConfigUpdateRequestWithAccountId>({
      query: ({ accountId, config }) => ({
        url: `/v2/accounts/${accountId}/mfa/config`,
        method: 'POST',
        body: config,
      }),
      transformResponse: (response: { data: MFAConfig }) => response.data,
      invalidatesTags: ['MFAConfig'],
    }),
  }),
})

export const {
  useListFactorsQuery,
  useLazyListFactorsQuery,
  useGetFactorQuery,
  useLazyGetFactorQuery,
  useStartFactorChallengeMutation,
  useCreateFactorMutation,
  useUpdateFactorMutation,
  useDeleteFactorMutation,
  useVerifyFactorMutation,
  useLazyListMFAStatusesQuery,
  useGenerateRecoveryCodeForUserMutation,
  useGetConfigQuery,
  useLazyGetConfigQuery,
  useUpdateConfigMutation,
} = mfaApi
